import React, { memo, useEffect, useRef, useState } from 'react';
import invariant from 'tiny-invariant';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import type { DroppableChildrenParams } from './dndTypes';
import type {
  BaseEventPayload,
  DropTargetGetFeedbackArgs,
  DropTargetLocalizedData,
  ElementDragType,
} from '@atlaskit/pragmatic-drag-and-drop/dist/types/internal-types.js';

interface DroppableProps {
  children: ({ isDraggedOver, droppableRef }: DroppableChildrenParams) => React.ReactNode;
  params?: {
    onDragEnter?: (args: BaseEventPayload<ElementDragType> & DropTargetLocalizedData) => void;
    onDrag?: (args: BaseEventPayload<ElementDragType> & DropTargetLocalizedData) => void;
    onDropTargetChange?: (args: BaseEventPayload<ElementDragType> & DropTargetLocalizedData) => void;
    onDragLeave?: () => void;
    onDrop?: (args: BaseEventPayload<ElementDragType> & DropTargetLocalizedData) => void;
    canDrop?: ((args: DropTargetGetFeedbackArgs<ElementDragType>) => boolean) | undefined;
    getDropEffect?: (args: DropTargetGetFeedbackArgs<ElementDragType>) => 'copy' | 'move' | 'link';
  };
  data?: Record<string, unknown>;
}

const Droppable = memo(({ children, params, data }: DroppableProps) => {
  const droppableRef = useRef(null);
  const [isDraggedOver, setIsDraggedOver] = useState(false);

  useEffect(() => {
    const el = droppableRef.current;
    invariant(el, 'droppable ref is not defined');

    return dropTargetForElements({
      element: el,
      getData: () => data || {},
      onDragEnter: args => {
        setIsDraggedOver(true);
        params?.onDragEnter?.(args);
      },
      canDrop: params?.canDrop,
      getDropEffect: params?.getDropEffect,
      onDrag: params?.onDrag,
      onDropTargetChange: params?.onDropTargetChange,
      onDragLeave: () => {
        setIsDraggedOver(false);
        params?.onDragLeave?.();
      },
      onDrop: args => {
        setIsDraggedOver(false);
        params?.onDrop?.(args);
      },
    });
  }, [params, data]);

  return <>{children({ isDraggedOver, droppableRef })}</>;
});

export default Droppable;
